import { createSelector } from 'reselect'
import formatLesson from 'redux/selectors/helpers/lesson_formatter'
import { formatLibraryType } from 'helpers/libraryTypeFormatter'
import shouldHideStoreSelector from 'redux/selectors/user/user_store_access_selector'
import findLessonByID from 'helpers/findLessonByID'
import libraryObjectTypes from 'helpers/enums/entityTypes'
import { getLibraryObjectName } from 'helpers/store/getLibraryObjectName'
import getLessonIdFromEntity from 'helpers/store/getLessonIdFromEntity'
import activitiesCounter from 'helpers/interactive_video/activities_counter'
import mediaSlide from 'redux/selectors/media_slides/find_media_slide'
import lessonTypes from 'helpers/enums/lessonTypes'
import featureFlagsSelectorCreator from 'redux/selectors/feature_flags/feature_flags_selector'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import tooltipTypes from 'helpers/enums/tooltipTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import getAssetById from 'redux/selectors/assets/asset_selector'
import mediaTypes from 'helpers/enums/mediaTypes'
import lessonStatusTypes from 'helpers/enums/lessonStatusTypes'

const getPreview = state => state.preview
const getEntities = state => state.lessons
const getLti = state => state.lti

const featureFlagsSelector = featureFlagsSelectorCreator([
	featureFlagsTypes.VIDEO_LESSON_SHOW_TEACH_BUTTON,
	featureFlagsTypes.INTERACTIVE_VIDEO_PREVIEW_CREDITS_TAB,
	featureFlagsTypes.INTERACTIVE_VIDEO_PREVIEW_STANDARDS_TAB,
])

const storePreviewContainerSelector = createSelector(
	[getPreview, getEntities, getLti, shouldHideStoreSelector, mediaSlide, featureFlagsSelector, getAssetById],
	(preview, entities, lti, shouldHideStore, mediaSlide, featureFlags, getAsset) => {
		let promoted = preview.promoted.map(promotedItem => ({
			...promotedItem,
			entities: promotedItem.entities.map( entityId => findLessonByID(entityId, entities)),
		}))
		const libraryType = formatLibraryType(lti)
		let lessonData, bundleData, entityType, entityUID, presentationUid
		let sessionTypeLaunched = sessionTypes.UNSET
		const entity = findLessonByID(preview.entity_ID, entities)
		const isEntityPreviewable = entity && entity.data && entity.data.actions
		if (isEntityPreviewable) {
			const isTeachButtonActive = featureFlags[featureFlagsTypes.VIDEO_LESSON_SHOW_TEACH_BUTTON]
			const canEditLesson = entity.data.state === lessonStatusTypes.PUBLISHED
			lessonData = formatLesson(
				entity,
				libraryType,
				false,
				tooltipTypes.NONE,
				false,
				false,
				isTeachButtonActive,
				canEditLesson,
			)
			if (lessonData && lessonData.data) {
				presentationUid = lessonData.data.presentation_uid
				if (lessonData.data.type === lessonTypes.VIDEO) {
					const media = mediaSlide(presentationUid)
					const asset = getAsset(media.posterUid, mediaTypes.IMAGE)
					sessionTypeLaunched = preview.sessionLaunchedType
					promoted = promoted.length > 0 ? promoted[0].entities : []
					lessonData.data = {
						...lessonData.data,
						totalActivities: activitiesCounter(media),
						duration: media.duration,
						posterUrl: asset ? asset.sizes.medium.path : null,
						handouts: media.handouts,
						isExtraInformationLoaded: preview.isExtraInformationLoaded,
						credits: media.credits,
						shouldShowCreditsTab: featureFlags[featureFlagsTypes.INTERACTIVE_VIDEO_PREVIEW_CREDITS_TAB],
						shouldShowStandardsTab: featureFlags[featureFlagsTypes.INTERACTIVE_VIDEO_PREVIEW_STANDARDS_TAB],
					}
				}
			}
			entityType = getLibraryObjectName(entity.library_object_type)
			entityUID = getLessonIdFromEntity(entity.library_object_type, lessonData)
			if (entity.library_object_type === libraryObjectTypes.BUNDLE) {
				bundleData = {
					bundleId: entity.data.id,
					bundleLessons: entity.data.entities
						.map(entityUID => findLessonByID(entityUID, entities)),
				}
			}
		}

		return {
			entityUID,
			lessonData,
			bundleData,
			entityType,
			promoted,
			shouldShowRelatedContent: !shouldHideStore,
			lessonAlreadyAdded: preview.lessonAlreadyAdded,
			canAddToSchoolLibraryId: preview.canAddToSchoolLibraryId,
			canAddToDistrictLibraryId: preview.canAddToDistrictLibraryId,
			presentationUid,
			sessionTypeLaunched,
		}
	}
)

export default storePreviewContainerSelector
